/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { BasicCard } from '@coursera-web/cds-labs';

import a11yKeyPress from 'js/lib/a11yKeyPress';

import { Skeleton, Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { toProgramSkillSet } from 'bundles/enterprise-legacy-learner-home/links';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import type { TargetSkillProficiencySummary } from 'bundles/page-config-common/types/enterprise';

import _t from 'i18n!nls/program-home';

type SkillSetCardContentProps = {
  skillSummary: TargetSkillProficiencySummary;
  programSlug: string;
  index: number;
};

const styles = {
  fullHeightContainer: css`
    height: 100%;

    > div {
      height: 100%;
    }
  `,

  skillsetContainer: css`
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-spacing-200);
    padding: 16px 24px;
  `,

  marginBottom: css`
    margin-bottom: 4px;
  `,

  truncateText: css`
    max-height: calc(2 * var(--cds-line-height-300));
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    overflow-wrap: break-word;
  `,
};

export const SkillSetCard = ({ skillSummary, programSlug, index }: SkillSetCardContentProps) => {
  const track = useTracker();
  const { nextLevel } = useHeaderLevel();

  const href = toProgramSkillSet(programSlug, skillSummary.slug);

  const eventingV3SkillSetData = {
    skillsetCard: {
      index,
    },
    skillset: {
      id: skillSummary.id,
    },
  };

  const skillsetCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_skillset_card',
    eventingV3SkillSetData
  );

  const onSkillSetContainerClick = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      redirectClientOrServer(href);
      track('click_skillset_card', eventingV3SkillSetData);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [href]
  );

  const formatSkillList = (skills: (string | undefined)[]) => {
    if (skills.length === 0) return '';
    if (skills.length === 1) return skills[0];
    if (skills.length === 2) return `${skills[0]} and ${skills[1]}`;
    return `${skills.slice(0, -1).join(', ')}, and ${skills[skills.length - 1]}`;
  };

  const skillList = formatSkillList(skillSummary.targetSkillProficiencies.map((skill) => skill?.skillName));

  return (
    <div ref={skillsetCardRef} css={styles.fullHeightContainer}>
      <BasicCard
        tabIndex={0}
        data-testid="skillset-card-container"
        css={styles.skillsetContainer}
        role="link"
        onClick={onSkillSetContainerClick}
        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => a11yKeyPress(event, onSkillSetContainerClick)}
      >
        <Typography2 variant="subtitleMedium" component={nextLevel} css={[styles.marginBottom, styles.truncateText]}>
          {skillSummary.title}
        </Typography2>
        <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.truncateText}>
          {_t("Skills you'll gain: #{skills}", { skills: skillList })}
        </Typography2>
      </BasicCard>
    </div>
  );
};

export const SkillSetCardPlaceholder = () => {
  return (
    <div className="skillset-container" aria-hidden data-testid="skillset-card-placeholder">
      <BasicCard css={styles.skillsetContainer}>
        <Skeleton width="70%" />
        <Skeleton width="100%" />
        <Skeleton width="90%" />
      </BasicCard>
    </div>
  );
};
